/**
 * AdditionalGroupsContent - Gestão de grupos de adicionais
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusCircle, Plus, Edit, Trash2, Layers, Package, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const AdditionalGroupsContent = () => {
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isAdditionalsModalOpen, setIsAdditionalsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroupForCategories, setSelectedGroupForCategories] = useState(null);
  const [selectedGroupForAdditionals, setSelectedGroupForAdditionals] = useState(null);
  const [linkedCategories, setLinkedCategories] = useState([]);
  const [groupAdditionals, setGroupAdditionals] = useState([]);
  const [linkingCategory, setLinkingCategory] = useState(null);
  const [togglingAdditional, setTogglingAdditional] = useState(null);
  const [showNewAdditionalForm, setShowNewAdditionalForm] = useState(false);
  const [newAdditionalData, setNewAdditionalData] = useState({ name: '', price: '' });
  const [creatingAdditional, setCreatingAdditional] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    min_choices: '0',
    max_choices: '10',
    is_required: false,
    limit_one_per_item: false
  });

  useEffect(() => {
    fetchGroups();
    fetchCategories();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setGroups(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLinkedCategories = async (groupId) => {
    try {
      const response = await axios.get(`${API}/additional-groups/${groupId}/linked-categories`);
      setLinkedCategories(response.data);
    } catch (error) {
      console.error('Error fetching linked categories:', error);
      setLinkedCategories([]);
    }
  };

  const handleLinkCategory = async (categoryId, isLinked) => {
    if (!selectedGroupForCategories) return;
    
    setLinkingCategory(categoryId);
    try {
      if (isLinked) {
        // Unlink
        const response = await axios.delete(`${API}/additional-groups/${selectedGroupForCategories.id}/link-category/${categoryId}`);
        toast.success(`${response.data.unlinked_count} produto(s) desvinculado(s) da categoria ${response.data.category_name}`);
      } else {
        // Link
        const response = await axios.post(`${API}/additional-groups/${selectedGroupForCategories.id}/link-category`, {
          category_id: categoryId
        });
        toast.success(`${response.data.linked_count} produto(s) vinculado(s) à categoria ${response.data.category_name}`);
      }
      // Refresh linked categories
      await fetchLinkedCategories(selectedGroupForCategories.id);
    } catch (error) {
      console.error('Error linking/unlinking category:', error);
      toast.error(error.response?.data?.detail || 'Erro ao vincular/desvincular categoria');
    } finally {
      setLinkingCategory(null);
    }
  };

  const openCategoryModal = async (group) => {
    setSelectedGroupForCategories(group);
    await fetchLinkedCategories(group.id);
    setIsCategoryModalOpen(true);
  };

  // === Funções para gerenciar adicionais do grupo ===
  const fetchGroupAdditionals = async (groupId) => {
    try {
      const response = await axios.get(`${API}/additional-groups/${groupId}/additionals`);
      setGroupAdditionals(response.data.additionals || []);
    } catch (error) {
      console.error('Error fetching group additionals:', error);
      setGroupAdditionals([]);
    }
  };

  const openAdditionalsModal = async (group) => {
    setSelectedGroupForAdditionals(group);
    await fetchGroupAdditionals(group.id);
    setIsAdditionalsModalOpen(true);
  };

  const handleToggleAdditional = async (additionalId, isLinked) => {
    if (!selectedGroupForAdditionals) return;
    
    setTogglingAdditional(additionalId);
    try {
      const response = await axios.patch(
        `${API}/additionals/${additionalId}/toggle-group/${selectedGroupForAdditionals.id}`
      );
      
      if (response.data.is_linked) {
        toast.success(`"${response.data.additional_name}" vinculado ao grupo`);
      } else {
        toast.success(`"${response.data.additional_name}" desvinculado do grupo`);
      }
      
      // Refresh the list
      await fetchGroupAdditionals(selectedGroupForAdditionals.id);
      // Also refresh groups to update count
      fetchGroups();
    } catch (error) {
      console.error('Error toggling additional:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar vinculação');
    } finally {
      setTogglingAdditional(null);
    }
  };

  const handleCreateQuickAdditional = async (e) => {
    e.preventDefault();
    if (!selectedGroupForAdditionals || !newAdditionalData.name || !newAdditionalData.price) return;
    
    setCreatingAdditional(true);
    try {
      // Create the additional already linked to this group
      await axios.post(`${API}/additionals`, {
        name: newAdditionalData.name,
        description: '',
        price: parseFloat(newAdditionalData.price),
        group_ids: [selectedGroupForAdditionals.id],
        available: true
      });
      
      toast.success(`"${newAdditionalData.name}" criado e vinculado ao grupo`);
      
      // Reset form and refresh
      setNewAdditionalData({ name: '', price: '' });
      setShowNewAdditionalForm(false);
      await fetchGroupAdditionals(selectedGroupForAdditionals.id);
      fetchGroups();
    } catch (error) {
      console.error('Error creating additional:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar adicional');
    } finally {
      setCreatingAdditional(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const groupData = {
        name: formData.name,
        description: formData.description,
        min_choices: parseInt(formData.min_choices),
        max_choices: parseInt(formData.max_choices),
        is_required: formData.is_required,
        limit_one_per_item: formData.limit_one_per_item
      };

      if (editingGroup) {
        await axios.put(`${API}/additional-groups/${editingGroup.id}`, groupData);
      } else {
        await axios.post(`${API}/additional-groups`, groupData);
      }

      setIsModalOpen(false);
      setFormData({ name: '', description: '', min_choices: '0', max_choices: '10', is_required: false, limit_one_per_item: false });
      setEditingGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Error saving group:', error);
      alert(error.response?.data?.detail || 'Erro ao salvar grupo');
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name || '',
      description: group.description || '',
      min_choices: group.min_choices != null ? group.min_choices.toString() : '0',
      max_choices: group.max_choices != null ? group.max_choices.toString() : '1',
      is_required: group.is_required !== false,
      limit_one_per_item: group.limit_one_per_item || false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (groupId) => {
    if (window.confirm('Tem certeza que deseja excluir este grupo?')) {
      try {
        await axios.delete(`${API}/additional-groups/${groupId}`);
        fetchGroups();
      } catch (error) {
        console.error('Error deleting group:', error);
        alert(error.response?.data?.detail || 'Erro ao excluir grupo');
      }
    }
  };

  const openModal = () => {
    setEditingGroup(null);
    setFormData({ name: '', description: '', min_choices: '0', max_choices: '10', is_required: false, limit_one_per_item: false });
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Grupos de Adicionais</h1>
          <p className="text-gray-400 mt-2">Organize seus adicionais em grupos com controle de escolhas mínimas e máximas</p>
        </div>
        <button onClick={openModal} className="glass-button-primary">
          <PlusCircle size={18} className="mr-2" />
          Novo Grupo
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="glass-card text-center py-12">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum grupo cadastrado</h3>
          <p className="text-gray-400 mb-6">Crie grupos para organizar seus adicionais com regras personalizadas</p>
          <button onClick={openModal} className="glass-button-primary">
            Criar Primeiro Grupo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group) => (
            <div key={group.id} className="glass-card group-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                    <Package size={24} className="text-white" />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => openAdditionalsModal(group)}
                    className="p-2 text-gray-400 hover:text-amber-400 transition-colors"
                    title="Gerenciar Adicionais"
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    onClick={() => openCategoryModal(group)}
                    className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                    title="Vincular a Categorias"
                  >
                    <Layers size={16} />
                  </button>
                  <button
                    onClick={() => handleEdit(group)}
                    className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                    title="Editar Grupo"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(group.id)}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                    title="Excluir Grupo"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white mb-2">{group.name}</h3>
              {group.description && (
                <p className="text-gray-400 text-sm mb-4">{group.description}</p>
              )}

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="text-blue-400 font-bold text-xs">MIN</span>
                    </div>
                    <span className="text-gray-300">Mínimo</span>
                  </div>
                  <span className="text-white font-bold text-lg">{group.min_choices}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <span className="text-green-400 font-bold text-xs">MAX</span>
                    </div>
                    <span className="text-gray-300">Máximo</span>
                  </div>
                  <span className="text-white font-bold text-lg">{group.max_choices}</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                <div className="flex items-center space-x-2 text-gray-400 text-sm">
                  <Plus size={16} />
                  <span>{group.additionals_count || 0} adicionais</span>
                </div>
                <div className="flex items-center gap-2">
                  {group.is_required && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full font-medium">
                      Obrigatório
                    </span>
                  )}
                  {!group.is_required && (
                    <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded-full font-medium">
                      Opcional
                    </span>
                  )}
                  {group.limit_one_per_item && (
                    <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full font-medium">
                      1 por item
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome do Grupo *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="glass-input w-full"
                placeholder="Ex: Ponto da Carne, Molhos, Extras"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="glass-input w-full resize-none"
                placeholder="Descrição opcional do grupo"
                rows="2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mínimo de Escolhas
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_choices}
                  onChange={(e) => setFormData({ ...formData, min_choices: e.target.value })}
                  className="glass-input w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">0 = opcional</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Máximo de Escolhas
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_choices}
                  onChange={(e) => setFormData({ ...formData, max_choices: e.target.value })}
                  className="glass-input w-full"
                  required
                />
              </div>
            </div>

            <div className="flex items-center p-4 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="is_required"
                checked={formData.is_required}
                onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500"
              />
              <label htmlFor="is_required" className="ml-3 text-sm text-gray-300">
                Grupo obrigatório (cliente deve escolher no mínimo definido)
              </label>
            </div>

            <div className="flex items-center p-4 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="limit_one_per_item"
                checked={formData.limit_one_per_item}
                onChange={(e) => setFormData({ ...formData, limit_one_per_item: e.target.checked })}
                className="w-4 h-4 text-cyan-500 bg-gray-700 border-gray-600 rounded focus:ring-cyan-500"
              />
              <label htmlFor="limit_one_per_item" className="ml-3 text-sm text-gray-300">
                <span className="font-medium text-cyan-400">Limitar 1 unidade por item</span>
                <span className="block text-xs text-gray-400 mt-0.5">Cliente não pode repetir o mesmo adicional (ex: não pode pedir 3x Banana)</span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingGroup(null);
                  setFormData({ name: '', description: '', min_choices: '0', max_choices: '10', is_required: false, limit_one_per_item: false });
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Vinculação a Categorias */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Layers size={20} className="text-green-400" />
              Vincular a Categorias
            </DialogTitle>
            {selectedGroupForCategories && (
              <p className="text-gray-400 text-sm mt-1">
                Grupo: <span className="text-white font-medium">{selectedGroupForCategories.name}</span>
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-gray-400 text-sm">
              Selecione as categorias para vincular este grupo de adicionais a <strong>todos os produtos</strong> da categoria.
            </p>
            
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {linkedCategories.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Carregando categorias...</p>
              ) : (
                linkedCategories.map((category) => {
                  const isFullyLinked = category.is_fully_linked || false;
                  const isPartiallyLinked = category.is_partially_linked || false;
                  const linkedCount = category.linked_products || 0;
                  const totalCount = category.total_products || 0;
                  const isLoading = linkingCategory === category.category_id;
                  
                  return (
                    <div 
                      key={category.category_id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                        isFullyLinked 
                          ? 'bg-green-500/20 border border-green-500/30' 
                          : isPartiallyLinked
                          ? 'bg-yellow-500/20 border border-yellow-500/30'
                          : 'bg-gray-800/50 border border-gray-700/50 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isFullyLinked ? 'bg-green-500/30' : isPartiallyLinked ? 'bg-yellow-500/30' : 'bg-gray-700'
                        }`}>
                          <Package size={18} className={
                            isFullyLinked ? 'text-green-400' : isPartiallyLinked ? 'text-yellow-400' : 'text-gray-400'
                          } />
                        </div>
                        <div>
                          <p className="text-white font-medium">{category.category_name}</p>
                          <p className="text-gray-400 text-xs">
                            {linkedCount} de {totalCount} produtos vinculados
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {isPartiallyLinked && (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                            Parcial
                          </span>
                        )}
                        <button
                          onClick={() => handleLinkCategory(category.category_id, isFullyLinked || isPartiallyLinked)}
                          disabled={isLoading || totalCount === 0}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            isLoading
                              ? 'bg-gray-600 text-gray-400 cursor-wait'
                              : isFullyLinked || isPartiallyLinked
                              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                              : totalCount === 0
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              Processando...
                            </>
                          ) : isFullyLinked || isPartiallyLinked ? (
                            <>
                              <X size={16} />
                              Desvincular
                            </>
                          ) : (
                            <>
                              <Check size={16} />
                              Vincular
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <div className="flex justify-end pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setIsCategoryModalOpen(false);
                  setSelectedGroupForCategories(null);
                  setLinkedCategories([]);
                }}
                className="glass-button-primary"
              >
                Concluir
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Gerenciar Adicionais do Grupo */}
      <Dialog open={isAdditionalsModalOpen} onOpenChange={setIsAdditionalsModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Plus size={20} className="text-amber-400" />
              Adicionais do Grupo
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Grupo: <strong className="text-white">{selectedGroupForAdditionals?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Botão para criar novo adicional */}
            {!showNewAdditionalForm ? (
              <button
                onClick={() => setShowNewAdditionalForm(true)}
                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/10 hover:border-green-500/50 transition-all"
              >
                <Plus size={18} />
                Criar Novo Adicional
              </button>
            ) : (
              <form onSubmit={handleCreateQuickAdditional} className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-green-400 text-sm font-medium mb-2">
                  <Plus size={16} />
                  Novo Adicional
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Nome do adicional"
                    value={newAdditionalData.name}
                    onChange={(e) => setNewAdditionalData({...newAdditionalData, name: e.target.value})}
                    className="glass-input text-sm"
                    required
                    autoFocus
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      value={newAdditionalData.price}
                      onChange={(e) => setNewAdditionalData({...newAdditionalData, price: e.target.value})}
                      className="glass-input text-sm pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewAdditionalForm(false);
                      setNewAdditionalData({ name: '', price: '' });
                    }}
                    className="flex-1 px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creatingAdditional || !newAdditionalData.name || !newAdditionalData.price}
                    className="flex-1 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {creatingAdditional ? (
                      <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Check size={16} />
                        Criar e Vincular
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
            
            {/* Lista de adicionais com checklist */}
            <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2">
              {groupAdditionals.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {showNewAdditionalForm ? 'Crie seu primeiro adicional acima' : 'Nenhum adicional cadastrado. Crie um novo!'}
                </p>
              ) : (
                groupAdditionals.map((additional) => {
                  const isLoading = togglingAdditional === additional.id;
                  
                  return (
                    <div 
                      key={additional.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                        additional.is_linked 
                          ? 'bg-amber-500/20 border border-amber-500/30' 
                          : 'bg-gray-800/50 border border-gray-700/50 hover:border-gray-600'
                      }`}
                      onClick={() => !isLoading && handleToggleAdditional(additional.id, additional.is_linked)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                          additional.is_linked 
                            ? 'bg-amber-500 border-amber-500' 
                            : 'border-gray-500 hover:border-gray-400'
                        }`}>
                          {isLoading ? (
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : additional.is_linked ? (
                            <Check size={14} className="text-white" />
                          ) : null}
                        </div>
                        <div>
                          <p className={`font-medium ${additional.is_linked ? 'text-white' : 'text-gray-300'}`}>
                            {additional.name}
                          </p>
                          {additional.other_groups_count > 0 && !additional.is_linked && (
                            <p className="text-xs text-gray-500">
                              Em {additional.other_groups_count} outro(s) grupo(s)
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${additional.is_linked ? 'text-amber-400' : 'text-gray-400'}`}>
                          R$ {additional.price?.toFixed(2)}
                        </span>
                        {!additional.available && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            Indisponível
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <div className="flex justify-end pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setIsAdditionalsModalOpen(false);
                  setSelectedGroupForAdditionals(null);
                  setGroupAdditionals([]);
                  setShowNewAdditionalForm(false);
                  setNewAdditionalData({ name: '', price: '' });
                }}
                className="glass-button-primary"
              >
                Concluir
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default AdditionalGroupsContent;
